import { Injectable } from "@angular/core";

@Injectable({
  providedIn: "root",
})
export class ReadOnlyTokenStorage {
  private static readonly STORAGE_KEY = "read_access_token";
  private static readonly SCOPE_STORAGE_KEY = "read_access_scope";
  private static readonly PROJECT_ID_STORAGE_KEY = "read_access_project_id";
  private static readonly LANGUAGE_STORAGE_KEY = "read_access_language";

  set(token: string) {
    sessionStorage.setItem(ReadOnlyTokenStorage.STORAGE_KEY, token);
  }

  get(): string | null {
    return sessionStorage.getItem(ReadOnlyTokenStorage.STORAGE_KEY);
  }

  clear() {
    sessionStorage.removeItem(ReadOnlyTokenStorage.STORAGE_KEY);
    sessionStorage.removeItem(ReadOnlyTokenStorage.SCOPE_STORAGE_KEY);
    sessionStorage.removeItem(ReadOnlyTokenStorage.PROJECT_ID_STORAGE_KEY);
    sessionStorage.removeItem(ReadOnlyTokenStorage.LANGUAGE_STORAGE_KEY);
  }

  setProjectId(projectId: string, readAccessToken: string) {
    if (!readAccessToken || !projectId) {
      return;
    }

    const ids = this.getStoredProjectIds();
    ids[readAccessToken] = projectId;
    sessionStorage.setItem(ReadOnlyTokenStorage.PROJECT_ID_STORAGE_KEY, JSON.stringify(ids));
  }

  getProjectId(readAccessToken: string): string | null {
    if (!readAccessToken) {
      return null;
    }

    const ids = this.getStoredProjectIds();
    return ids[readAccessToken] ?? null;
  }

  private getStoredProjectIds(): Record<string, string> {
    const stored = sessionStorage.getItem(ReadOnlyTokenStorage.PROJECT_ID_STORAGE_KEY);
    if (!stored) {
      return {};
    }
    try {
      const parsed = JSON.parse(stored);
      if (typeof parsed === "object" && parsed !== null) {
        return parsed as Record<string, string>;
      }
    } catch {
      return {};
    }
    return {};
  }

  setBusinessScope(scope: string, readAccessToken: string) {
    if (!readAccessToken) {
      return;
    }

    const scopes = this.getBusinessScopes();
    scopes[readAccessToken] = scope;
    sessionStorage.setItem(ReadOnlyTokenStorage.SCOPE_STORAGE_KEY, JSON.stringify(scopes));
  }

  getBusinessScope(readAccessToken: string): string | null {
    if (!readAccessToken) {
      return null;
    }

    const storedScopes = sessionStorage.getItem(ReadOnlyTokenStorage.SCOPE_STORAGE_KEY);
    if (!storedScopes) {
      return null;
    }

    if (storedScopes === "FGDS" || storedScopes === "MONITORING") {
      return storedScopes;
    }

    const scopes = this.getBusinessScopes();
    return scopes[readAccessToken] ?? null;
  }

  private getBusinessScopes(): Record<string, string> {
    const storedScopes = sessionStorage.getItem(ReadOnlyTokenStorage.SCOPE_STORAGE_KEY);

    if (!storedScopes) {
      return {};
    }

    try {
      const parsed = JSON.parse(storedScopes);
      if (typeof parsed === "object" && parsed !== null) {
        return parsed as Record<string, string>;
      }
    } catch {
      return {};
    }

    return {};
  }

  setLanguage(language: string) {
    sessionStorage.setItem(ReadOnlyTokenStorage.LANGUAGE_STORAGE_KEY, language);
  }

  getLanguage(): string | null {
    return sessionStorage.getItem(ReadOnlyTokenStorage.LANGUAGE_STORAGE_KEY);
  }
}
